/**
 * PATH: packages/email-hub/src/feature/components/CardVisibilityBadge.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-CARDVISIBILITYBADGE-001
 * PURPOSE: The "below card" highlight that says whether an AI-filed
 *          email is still PRIVATE TO YOU (pending suggestion, not on the
 *          shared board) or ON THE BOARD (visible to the team).
 * WHY:     Operator 2026-06-16: "a clear highlight below card that shows
 *          it as private / not private". Maps to the
 *          ai_auto_apply_allocations flow — a pending AI suggestion is
 *          private until accepted; once accepted/auto-applied it is on
 *          the shared board.
 * FLOW:    CardVisibility -> <span> (lock/users icon + label). Renders
 *          nothing for 'none' (rows with no board placement).
 * RELATED:
 *   - lib/cardAiState.ts: resolveCardAiState -> `visibility`.
 */
import type { ReactElement } from 'react'
import { Lock, Users } from 'lucide-react'

import type { CardVisibility } from '../lib/cardAiState'
import { cn } from '../../_internal/lib/utils'

export function CardVisibilityBadge({
  visibility,
  className,
  compact = false,
}: {
  visibility: CardVisibility
  className?: string
  /** Inbox-list mode: render ONLY the lock/users glyph (no label) so the
   *  signal folds into the row's chip line instead of a wide pill on its own
   *  line. The leading AI pip's colour already names private vs on-board; this
   *  is the quiet, glanceable reinforcement (full label stays in the reader). */
  compact?: boolean
}): ReactElement | null {
  if (visibility === 'none') return null
  const isPrivate = visibility === 'private'
  const label = isPrivate ? 'Private to you' : 'On board · team'
  const fullTitle = isPrivate
    ? 'Private to you - this AI suggestion is not on the shared board until you accept it.'
    : 'On the board - visible to board members.'
  if (compact) {
    return (
      <span
        data-testid="card-visibility-badge"
        data-visibility={visibility}
        data-compact="true"
        title={fullTitle}
        className={cn(
          'inline-flex items-center justify-center h-4 w-4 shrink-0 rounded-full',
          isPrivate
            ? 'text-amber-600 dark:text-amber-300'
            : 'text-emerald-600 dark:text-emerald-300',
          className,
        )}
      >
        {isPrivate ? (
          <Lock className="h-3 w-3" aria-hidden />
        ) : (
          <Users className="h-3 w-3" aria-hidden />
        )}
        <span className="sr-only">{label}</span>
      </span>
    )
  }
  return (
    <span
      data-testid="card-visibility-badge"
      data-visibility={visibility}
      title={fullTitle}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium leading-none',
        isPrivate
          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
          : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300',
        className,
      )}
    >
      {isPrivate ? (
        <Lock className="h-2.5 w-2.5 shrink-0" aria-hidden />
      ) : (
        <Users className="h-2.5 w-2.5 shrink-0" aria-hidden />
      )}
      {label}
    </span>
  )
}

export default CardVisibilityBadge
